import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Server, Database, Activity, RefreshCw, Package, HardDrive, Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

import { API } from '../../lib/api';

const DEPLOYMENT_FIELD_NOTES = {
  'Deployment Type': 'How the platform is running — `kubernetes` (Helm chart on a cluster), `docker-compose` (single host), or `bare-metal`.',
  'Version': 'Build version of the platform binary currently serving traffic.',
  'Namespace': 'Kubernetes namespace (or logical grouping) the platform is installed under.',
  'Cloud Provider': 'Where this platform itself is hosted — AWS / Azure / GCP / on-prem.',
};

const PIPELINE_METRIC_NOTES = {
  'Ingestion Rate': 'Messages per second the streaming pipeline is currently consuming from the event bus. A sustained drop usually means an upstream collector is down.',
  'Processing Lag': 'Delay between event arrival and successful processing. Under 200 ms is healthy; spikes indicate the pipeline is falling behind.',
};

const InfoTip = ({ text }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex" aria-label="More info">
          <Info className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground transition-colors" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const PlatformHealth = () => {
  const [deploymentInfo, setDeploymentInfo] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [deployRes, pipelineRes] = await Promise.all([
        axios.get(`${API}/admin/deployment`),
        axios.get(`${API}/admin/pipeline/status`),
      ]);
      setDeploymentInfo(deployRes.data);
      setPipelineStatus(pipelineRes.data);
    } catch (error) {
      console.error('Error fetching platform health:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const pipelineComponents = pipelineStatus ? [
    {
      key: 'kafka',
      icon: Activity,
      ...(pipelineStatus.kafka || {}),
    },
    {
      key: 'clickhouse',
      icon: Database,
      ...(pipelineStatus.clickhouse || {}),
    },
    {
      key: 'redis',
      icon: HardDrive,
      ...(pipelineStatus.redis || {}),
    },
    {
      key: 'opensearch',
      icon: Server,
      ...(pipelineStatus.opensearch || {}),
    },
  ] : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platform-health-page">
      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-['Chivo']" data-testid="platform-health-title">Platform Health</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Real-time status of the platform's own internal services — the streaming pipeline,
            cache, search index and database that power every dashboard you see.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Deployment info */}
      <Card className="bg-card border-border" data-testid="deployment-info">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Platform Deployment
            <InfoTip text="Where and how this platform is running. Useful when filing support tickets or coordinating upgrades." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Deployment Type', value: deploymentInfo?.deployment_type, transform: 'capitalize' },
              { label: 'Version', value: deploymentInfo?.version },
              { label: 'Namespace', value: deploymentInfo?.namespace, mono: true },
              { label: 'Cloud Provider', value: (deploymentInfo?.cloud_provider || 'N/A'), transform: 'uppercase' },
            ].map((f) => (
              <div key={f.label} className="p-4 bg-secondary/50 rounded-lg">
                <div className="flex items-center justify-between gap-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{f.label}</p>
                  <InfoTip text={DEPLOYMENT_FIELD_NOTES[f.label] || ''} />
                </div>
                <p className={`text-lg font-semibold mt-1 ${f.transform === 'capitalize' ? 'capitalize' : ''} ${f.transform === 'uppercase' ? 'uppercase' : ''} ${f.mono ? 'font-mono' : ''}`}>
                  {f.value || '—'}
                </p>
              </div>
            ))}
          </div>

          {deploymentInfo?.modules_enabled?.length > 0 && (
            <div className="mt-6">
              <p className="text-sm text-muted-foreground mb-3">Enabled Modules</p>
              <div className="flex flex-wrap gap-2">
                {deploymentInfo.modules_enabled.map((module) => (
                  <Badge key={module} variant="secondary" className="bg-primary/10 text-primary">
                    {module}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Helm Release</p>
              <p className="font-mono">{deploymentInfo?.helm_release || '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Install Date</p>
              <p className="font-mono">
                {deploymentInfo?.install_date ? new Date(deploymentInfo.install_date).toLocaleDateString() : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline & Services */}
      <Card className="bg-card border-border" data-testid="pipeline-status">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Backbone Services
            <InfoTip text="Backing services for event streaming, analytics, caching and search. Each card shows whether the service is currently deployed in your environment and what it would do if you turned it on." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
            {pipelineComponents.map((component) => {
              const ComponentIcon = component.icon;
              const state = component.state || 'not_deployed';
              const isDeployed = state === 'deployed';
              const isInProcess = state === 'in_process';
              const stateBadge = {
                deployed: { label: 'Deployed', cls: 'bg-green-500/15 text-green-500 border-green-500/30' },
                in_process: { label: 'In-process fallback', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
                not_deployed: { label: 'Not deployed', cls: 'bg-secondary text-muted-foreground border-border' },
              }[state] || { label: state, cls: 'bg-secondary text-muted-foreground border-border' };

              const cardCls = isDeployed
                ? 'border-green-500/30 bg-green-500/5'
                : isInProcess
                ? 'border-blue-500/30 bg-blue-500/5'
                : 'border-border bg-secondary/20';

              return (
                <div
                  key={component.key}
                  className={`p-4 rounded-lg border flex flex-col gap-3 ${cardCls}`}
                  data-testid={`pipeline-${component.key}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <ComponentIcon className="w-5 h-5 text-muted-foreground" />
                      <span className="font-medium">{component.label || component.key}</span>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${stateBadge.cls}`}>
                      {stateBadge.label}
                    </Badge>
                  </div>

                  {/* Currently */}
                  {component.current_implementation && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Currently</p>
                      <p className="text-xs text-foreground/90 mt-0.5">{component.current_implementation}</p>
                    </div>
                  )}

                  {/* Intended role */}
                  {component.intended_role && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">If deployed, would handle</p>
                      <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">{component.intended_role}</p>
                    </div>
                  )}

                  {/* Deploy when */}
                  {component.needed_when && !isDeployed && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-[10px] uppercase tracking-wide text-amber-400/80">Deploy when</p>
                      <p className="text-[11px] text-foreground/80 leading-relaxed mt-0.5">{component.needed_when}</p>
                    </div>
                  )}

                  {/* Alternatives + env var */}
                  {!isDeployed && (component.alternatives?.length || component.env_var) && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      {component.alternatives?.slice(0, 4).map((alt) => (
                        <Badge
                          key={alt}
                          variant="outline"
                          className="text-[10px] border-border text-muted-foreground"
                        >
                          {alt}
                        </Badge>
                      ))}
                      {component.env_var && (
                        <span className="text-[10px] font-mono text-muted-foreground/80 ml-auto">
                          {component.env_var}=…
                        </span>
                      )}
                    </div>
                  )}

                  {/* Metrics, only when actually deployed */}
                  {isDeployed && (
                    <div className="text-xs text-muted-foreground pt-2 border-t border-border/50">
                      {component.key === 'kafka' && `${component.topics ?? 0} topics · ${(component.messages_per_sec ?? 0).toLocaleString()} msg/s`}
                      {component.key === 'clickhouse' && `${component.tables ?? 0} tables · ${((component.rows_total ?? 0) / 1e9).toFixed(2)}B rows`}
                      {component.key === 'redis' && `${component.memory_used_mb ?? 0} MB · ${(component.keys ?? 0).toLocaleString()} keys`}
                      {component.key === 'opensearch' && `${component.indices ?? 0} indices · ${((component.docs_total ?? 0) / 1e6).toFixed(0)}M docs`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Ingestion metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">Ingestion Rate</span>
                  <InfoTip text={PIPELINE_METRIC_NOTES['Ingestion Rate']} />
                </div>
                <span className="text-lg font-bold text-primary">
                  {(pipelineStatus?.ingestion_rate ?? 0).toLocaleString()} msg/s
                </span>
              </div>
              <Progress value={75} className="h-2" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">Processing Lag</span>
                  <InfoTip text={PIPELINE_METRIC_NOTES['Processing Lag']} />
                </div>
                <span className={`text-lg font-bold ${(pipelineStatus?.processing_lag_ms ?? 0) < 200 ? 'text-green-500' : 'text-yellow-500'}`}>
                  {pipelineStatus?.processing_lag_ms ?? 0}ms
                </span>
              </div>
              <Progress value={(pipelineStatus?.processing_lag_ms ?? 0) < 200 ? 20 : 60} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Architecture diagram */}
      <Card className="bg-card border-border" data-testid="architecture">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            Platform Architecture
            <InfoTip text="High-level layout of how data flows: Agents push telemetry → API gateway → streaming pipeline → analytics & storage → UI." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-secondary/30 rounded-lg p-6 font-mono text-xs sm:text-sm overflow-x-auto">
            <pre className="text-muted-foreground whitespace-pre">
{`┌─────────────────────────────────────────────────────────────────┐
│                    CloudGenie Intelligence Platform               │
│  ┌─────────────┐  ┌─────────────┐  ┌─────────────┐              │
│  │     UI      │  │     API     │  │  Ingestion  │              │
│  │  Dashboard  │◄─┤   Gateway   │◄─┤   Service   │◄─── Agents   │
│  └─────────────┘  └──────┬──────┘  └──────┬──────┘              │
│                          │                │                     │
│  ┌─────────────┐  ┌──────▼──────┐  ┌──────▼──────┐              │
│  │ Intelligence│  │  Analytics  │  │    Kafka    │              │
│  │   Engine    │  │   Engine    │  │   Pipeline  │              │
│  └─────────────┘  └──────┬──────┘  └─────────────┘              │
│                          │                                      │
│  ┌─────────────┐  ┌──────▼──────┐  ┌─────────────┐              │
│  │  Automation │  │ ClickHouse  │  │    Redis    │              │
│  │   Engine    │  │   MongoDB   │  │  OpenSearch │              │
│  └─────────────┘  └─────────────┘  └─────────────┘              │
└─────────────────────────────────────────────────────────────────┘`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformHealth;
